import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from '@/hooks/useDebounce'
import { FiSearch, FiChevronDown, FiChevronRight, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi'
import DispatchDetailsModal from './DispatchDetailsModal'
import DispatchDocumentFilesModal from './DispatchDocumentFilesModal'
import AddDispatchModal from './AddDispatchModal'
import AddSubDataModal from './AddSubDataModal'
import AddTruckRegistrationModal from './AddTruckRegistrationModal'
import TruckRegistrationTable from './TruckRegistrationTable'
import PdfViewerModal from '@/pages/finance/PdfViewerModal'
import PageSizeSelect from '@/components/common/PageSizeSelect'
import { toast } from 'react-toastify'
import {
  getStockDispatches,
  getPendingUploadsSummary,
  getWarehouses,
  uploadDocuments,
  getDocumentFileUrl,
  deleteDocumentFile,
  deleteStockDispatch,
  hasDocumentUploaded,
  getDocumentButtonLabel,
  getDocumentTypeLabel,
  listDocumentFilesFromDispatch,
  countDocumentFiles,
  PendingUploadItem,
  StockDispatchApi,
  StockDispatchStatus,
  DocumentType,
  WarehouseApi,
  getTruckRegistrationEntries,
  getStatusColor,
  getStatusLabel,
  formatQty,
  formatCurrency,
  formatDate,
} from '@/services/api/stockDispatch'
import {
  flyteSyncCompleteMessage,
  startFlyteSync,
  subscribeFlyteSync,
  type FlyteSyncState,
} from '@/services/flyteSync'
import { getUser } from '@/utils/auth'
import { canUploadStockDispatchDocument, hasPermission } from '@/constants/permissions'
import type { UserRole as PermissionUserRole } from '@/types'

interface StockDispatchProps {
  pageTitle?: string
  addButtonLabel?: string
  breadcrumbLabel?: string
}

const StockDispatch = ({
  pageTitle = 'Stock Dispatch',
  addButtonLabel = 'Add Dispatch',
  breadcrumbLabel = 'Stock Dispatch',
}: StockDispatchProps) => {
  const userRole = (getUser()?.role ?? '') as PermissionUserRole
  const canManageDispatches = hasPermission(userRole, 'load_out:create')
  const [searchParams] = useSearchParams()
  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<StockDispatchStatus | ''>('')
  const [customerFilter, setCustomerFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const [pendingUploadsVisible, setPendingUploadsVisible] = useState(false)
  const [pendingUploads, setPendingUploads] = useState<PendingUploadItem[]>([])
  const [pendingUploadsIndex, setPendingUploadsIndex] = useState(0)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [isDispatchDetailsModalOpen, setIsDispatchDetailsModalOpen] = useState(false)
  const [selectedDispatch, setSelectedDispatch] = useState<StockDispatchApi | null>(null)
  const [openActionRow, setOpenActionRow] = useState<number | null>(null)
  const [deletingDispatchId, setDeletingDispatchId] = useState<string | null>(null)
  const [isAddDispatchModalOpen, setIsAddDispatchModalOpen] = useState(false)
  const [editingDispatch, setEditingDispatch] = useState<StockDispatchApi | null>(null)
  const [isAddSubDataModalOpen, setIsAddSubDataModalOpen] = useState(false)
  const [selectedDispatchForSubData, setSelectedDispatchForSubData] = useState<StockDispatchApi | null>(null)
  const [isAddTruckModalOpen, setIsAddTruckModalOpen] = useState(false)
  const [selectedDispatchForTruck, setSelectedDispatchForTruck] = useState<StockDispatchApi | null>(null)
  
  // Data state
  const [dispatches, setDispatches] = useState<StockDispatchApi[]>([])
  const [warehouses, setWarehouses] = useState<WarehouseApi[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalResults, setTotalResults] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [flyteJob, setFlyteJob] = useState<FlyteSyncState>(() => ({
    status: 'idle',
    fetched: 0,
    processed: 0,
    inserted: 0,
    updated: 0,
    linkedToOrders: 0,
    unlinked: 0,
    documentsSaved: 0,
    errors: 0,
    since: '2026-04-01',
    percent: 0,
  }))
  const [flyteSyncMessage, setFlyteSyncMessage] = useState<string | null>(null)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const autoRefreshMs = 45000

  // Upload state
  const [uploadingDoc, setUploadingDoc] = useState<{ dispatchId: string; type: DocumentType } | null>(null)
  const [documentFilesModal, setDocumentFilesModal] = useState<{
    dispatchId: string
    type: DocumentType
    dispatch: StockDispatchApi
  } | null>(null)
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)
  const [pdfViewer, setPdfViewer] = useState<{ url: string | null; title: string; loading: boolean }>({
    url: null,
    title: 'Document',
    loading: false,
  })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchDispatches = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true)
    setError(null)
    try {
      const response = await getStockDispatches({
        page: currentPage,
        limit: resultsPerPage,
        status: statusFilter || undefined,
        customer: customerFilter || undefined,
        search: debouncedSearch || undefined,
      })
      setDispatches(response.stockDispatches)
      setTotalResults(response.meta.total)
      setTotalPages(response.meta.totalPages)

      const pendingResponse = await getPendingUploadsSummary({
        status: statusFilter || undefined,
        customer: customerFilter || undefined,
        search: debouncedSearch || undefined,
      })
      setPendingUploads(pendingResponse.items)
      setPendingUploadsIndex((prev) => {
        if (pendingResponse.items.length === 0) return 0
        return Math.min(prev, pendingResponse.items.length - 1)
      })
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dispatches')
      setDispatches([])
      setPendingUploads([])
      setPendingUploadsIndex(0)
    } finally {
      setLoading(false)
    }
  }, [currentPage, statusFilter, customerFilter, debouncedSearch, resultsPerPage])

  // Fetch warehouses for filters
  const fetchWarehouses = useCallback(async () => {
    try {
      const warehouseList = await getWarehouses()
      setWarehouses(warehouseList)
    } catch (err) {
      console.error('Failed to fetch warehouses:', err)
    }
  }, [])

  // Initial load
  useEffect(() => {
    fetchDispatches()
    fetchWarehouses()
  }, [fetchDispatches, fetchWarehouses])

  // Auto-refresh live data (dispatches + pending uploads)
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void fetchDispatches({ silent: true })
      }
    }, autoRefreshMs)

    return () => window.clearInterval(intervalId)
  }, [fetchDispatches])

  // Show pending uploads notification if there are any
  useEffect(() => {
    if (pendingUploads.length > 0 && !pendingUploadsVisible) {
      setPendingUploadsVisible(true)
    }
  }, [pendingUploads.length])

  useEffect(() => {
    return subscribeFlyteSync((job) => {
      setFlyteJob(job)
      if (job.status === 'completed') {
        setFlyteSyncMessage(flyteSyncCompleteMessage(job))
        void fetchDispatches()
      }
      if (job.status === 'failed') {
        setFlyteSyncMessage(flyteSyncCompleteMessage(job))
      }
    })
  }, [fetchDispatches])

  const handleNextPendingUpload = () => {
    setPendingUploadsIndex((prev) => (prev + 1) % pendingUploads.length)
  }

  const handlePrevPendingUpload = () => {
    setPendingUploadsIndex((prev) => (prev - 1 + pendingUploads.length) % pendingUploads.length)
  }

  const toggleRowExpansion = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  const handleViewDispatch = (dispatch: StockDispatchApi) => {
    setSelectedDispatch(dispatch)
    setIsDispatchDetailsModalOpen(true)
  }

  const handleCloseDispatchDetails = () => {
    setIsDispatchDetailsModalOpen(false)
    setSelectedDispatch(null)
  }

  const handleToggleActions = (rowId: number) => {
    setOpenActionRow((prev) => (prev === rowId ? null : rowId))
  }

  const handleAddSubData = (dispatch: StockDispatchApi) => {
    setSelectedDispatchForSubData(dispatch)
    setIsAddSubDataModalOpen(true)
    setOpenActionRow(null)
  }

  const handleAddTruckRegistration = (dispatch: StockDispatchApi) => {
    setSelectedDispatchForTruck(dispatch)
    setIsAddTruckModalOpen(true)
    setOpenActionRow(null)
    const rowIndex = dispatches.findIndex((d) => d.id === dispatch.id)
    if (rowIndex >= 0) {
      setExpandedRows((prev) => new Set(prev).add(rowIndex))
    }
  }

  const handleEditDispatch = (dispatch: StockDispatchApi) => {
    setEditingDispatch(dispatch)
    setIsAddDispatchModalOpen(true)
    setOpenActionRow(null)
  }

  const handleDeleteDispatch = async (dispatch: StockDispatchApi) => {
    const label = dispatch.lotNumber || dispatch.product || dispatch.id.slice(0, 8)
    if (!window.confirm(`Delete stock dispatch "${label}"? This cannot be undone.`)) return

    try {
      setDeletingDispatchId(dispatch.id)
      await deleteStockDispatch(dispatch.id)
      setOpenActionRow(null)
      await fetchDispatches()
    } catch (_err: any) {
      // Global API toast handles user-facing error messaging.
    } finally {
      setDeletingDispatchId(null)
    }
  }

  // Document upload handling
  const handleUploadClick = (dispatchId: string, docType: DocumentType) => {
    setUploadingDoc({ dispatchId, type: docType })
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0 || !uploadingDoc) return

    try {
      await uploadDocuments(uploadingDoc.dispatchId, uploadingDoc.type, Array.from(fileList))
      fetchDispatches()
      if (documentFilesModal?.dispatchId === uploadingDoc.dispatchId && documentFilesModal.type === uploadingDoc.type) {
        setDocumentFilesModal(null)
      }
    } catch (_err: any) {
      // Global API toast handles user-facing error messaging.
    } finally {
      setUploadingDoc(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleViewDocument = async (
    dispatchId: string,
    docType: DocumentType,
    fileId?: string
  ) => {
    const title = getDocumentTypeLabel(docType)
    setPdfViewer({ url: null, title, loading: true })
    try {
      let resolvedFileId = fileId
      if (!resolvedFileId) {
        const item =
          dispatches.find((d) => d.id === dispatchId) ??
          (selectedDispatch?.id === dispatchId ? selectedDispatch : null) ??
          documentFilesModal?.dispatch
        const files = item ? listDocumentFilesFromDispatch(item, docType) : []
        resolvedFileId = files[0]?.id ?? 'primary'
      }
      const url = await getDocumentFileUrl(dispatchId, docType, resolvedFileId)
      if (!url) {
        setPdfViewer({ url: null, title, loading: false })
        toast.error('Could not open this document.')
        return
      }
      setPdfViewer({ url, title, loading: false })
    } catch (_err: any) {
      setPdfViewer({ url: null, title, loading: false })
      toast.error(_err?.message || 'Could not open this document.')
    }
  }

  const handleDocumentClick = (
    item: StockDispatchApi,
    docType: DocumentType,
    canUpload = canUploadStockDispatchDocument(userRole, docType)
  ) => {
    const count = countDocumentFiles(item, docType)
    if (count === 0) {
      if (canUpload) handleUploadClick(item.id, docType)
      return
    }
    if (count === 1) {
      void handleViewDocument(item.id, docType)
      return
    }
    setDocumentFilesModal({ dispatchId: item.id, type: docType, dispatch: item })
  }

  const handleDeleteDocumentFile = async (fileId: string) => {
    if (!documentFilesModal) return
    const { dispatchId, type } = documentFilesModal
    if (!window.confirm('Remove this file from the dispatch?')) return
    setDeletingFileId(fileId)
    try {
      const updated = await deleteDocumentFile(dispatchId, type, fileId)
      const remaining = listDocumentFilesFromDispatch(updated, type)
      if (remaining.length === 0) {
        setDocumentFilesModal(null)
      } else {
        setDocumentFilesModal({ dispatchId, type, dispatch: updated })
      }
      if (selectedDispatch?.id === dispatchId) {
        setSelectedDispatch(updated)
      }
      await fetchDispatches()
    } catch {
      // Global API toast handles user-facing error messaging.
    } finally {
      setDeletingFileId(null)
    }
  }

  const renderDocumentButton = (item: StockDispatchApi, docType: DocumentType) => {
    const uploaded = hasDocumentUploaded(item, docType)
    const canUpload = canUploadStockDispatchDocument(userRole, docType)
    const count = countDocumentFiles(item, docType)
    const label = getDocumentButtonLabel(item, docType)

    if (count === 0 && !canUpload) {
      return (
        <span style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A' }}>—</span>
      )
    }

    return (
      <button
        onClick={() => handleDocumentClick(item, docType, canUpload)}
        className="hover:opacity-90 transition-opacity"
        style={{
          fontFamily: 'Lexend Deca',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: uploaded ? '#13AB5B' : '#212126',
          backgroundColor: uploaded ? '#E8F7EF' : '#FFFFFF',
          border: uploaded ? '1px solid #13AB5B' : '1px solid #E9E9EA',
          borderRadius: '6px',
          padding: '6px 10px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    )
  }

  const handleDispatchCreated = () => {
    setIsAddDispatchModalOpen(false)
    fetchDispatches()
  }

  const handleFlyteSync = async () => {
    if (flyteJob.status === 'running') return
    setFlyteSyncMessage('Flyte sync started in the background. You can leave this page.')
    try {
      await startFlyteSync()
    } catch {
      setFlyteSyncMessage('Could not start Flyte sync.')
    }
  }

  const handleSubDataAdded = () => {
    setIsAddSubDataModalOpen(false)
    setSelectedDispatchForSubData(null)
    fetchDispatches()
  }

  const handleTruckRegistrationAdded = () => {
    setIsAddTruckModalOpen(false)
    setSelectedDispatchForTruck(null)
    fetchDispatches()
  }

  // Get unique customers for filter dropdown
  const uniqueCustomers = [...new Set(dispatches.map(d => d.customer).filter(Boolean))]

  return (
    <div className="space-y-4 sm:space-y-6 lg:max-w-[calc(100%-200px)] lg:ml-auto lg:mr-0" style={{ maxWidth: '100%' }}>
      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      />
      {/* Pending Uploads Notification */}
      {pendingUploadsVisible && pendingUploads.length > 0 && (
      <div className="flex justify-end">
        <div 
          className="bg-white rounded-lg border border-gray-200 relative overflow-hidden"
          style={{
            fontFamily: 'Lexend Deca',
            width: '369px',
            height: '127px',
            padding: '16px'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: '#EEC233'
            }}
          />
          <button
            onClick={() => setPendingUploadsVisible(false)}
            className="absolute top-3 right-3 flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ width: '24px', height: '24px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          
          <div className="flex flex-col h-full justify-between" style={{ paddingRight: '8px' }}>
            <div className="flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A' }}>
                  {pendingUploadsIndex + 1}/{pendingUploads.length}
                </span>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center rounded flex-shrink-0" style={{ width: '24px', height: '24px', backgroundColor: '#FEF9EB' }}>
                  <img src="/images/danger.svg" alt="Warning" style={{ width: '16px', height: '16px' }} />
                </div>
                <div style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                  {pendingUploads.length} Pending Uploads
                </div>
              </div>
              
              <div className="flex items-center gap-2" style={{ marginTop: '8px', marginLeft: '0px' }}>
                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A' }}>
                  {pendingUploads[pendingUploadsIndex]?.dispatchRef || pendingUploads[pendingUploadsIndex]?.dispatchId.slice(0, 8)} - {pendingUploads[pendingUploadsIndex]?.text}
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 12L10 8L6 4" stroke="#69697A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            
            <div className="flex items-center justify-start gap-2" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <button onClick={handlePrevPendingUpload} className="flex items-center justify-center hover:opacity-80 transition-opacity" style={{ width: '24px', height: '24px' }}>
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#69697A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button onClick={handleNextPendingUpload} className="flex items-center justify-center hover:opacity-80 transition-opacity" style={{ width: '24px', height: '24px' }}>
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#69697A" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
        <h1 className="text-lg sm:text-xl" style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', lineHeight: '32px', color: '#212126' }}>
            {pageTitle}
        </h1>
        <div className="sm:ml-auto flex flex-col items-end gap-2">
          <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', marginTop: '6px' }}>
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>{breadcrumbLabel}</span>
          </span>
          {canManageDispatches && (
            <div className="flex items-center gap-2">
            <button
              onClick={handleFlyteSync}
              disabled={flyteJob.status === 'running'}
              className="relative overflow-hidden flex items-center justify-center font-medium disabled:cursor-wait transition-opacity whitespace-nowrap"
              style={{
                height: '38px',
                minWidth: '220px',
                padding: '0 14px',
                color: flyteJob.status === 'running' && flyteJob.percent >= 45 ? '#FFFFFF' : '#212126',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D9D9DE',
                borderRadius: '6px',
                gap: '8px',
              }}
            >
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${flyteJob.status === 'idle' ? 0 : flyteJob.percent}%`,
                  backgroundColor: flyteJob.status === 'failed' ? '#FECACA' : '#137A43',
                  transition: 'width 400ms ease',
                }}
              />
              <FiRefreshCw className={`relative z-10 w-4 h-4 ${flyteJob.status === 'running' ? 'animate-spin' : ''}`} />
              <span className="relative z-10">
                {flyteJob.status === 'running'
                  ? `Syncing Flyte ${flyteJob.percent}%`
                  : flyteJob.status === 'completed'
                    ? 'Sync complete'
                    : flyteJob.status === 'failed'
                      ? 'Sync failed'
                      : 'Sync Flyte'}
              </span>
            </button>
            <button
              onClick={() => { setEditingDispatch(null); setIsAddDispatchModalOpen(true) }}
              className="flex items-center justify-center text-white font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ width: '150px', height: '38px', backgroundColor: '#212126', borderRadius: '6px', gap: '12px' }}
            >
              <FiPlus className="w-4 h-4" />
              <span>{addButtonLabel}</span>
            </button>
            </div>
          )}
        </div>
      </div>

      {flyteSyncMessage && (
        <div className="rounded-md border px-4 py-3 text-sm" style={{ borderColor: '#B9DEC9', backgroundColor: '#F1FAF5', color: '#137A43' }}>
          {flyteSyncMessage}
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Search and Filters */}
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="relative flex-1 w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search trip, lot, customer, MZGO..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', height: '40px' }}
              />
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as StockDispatchStatus | ''); setCurrentPage(1) }}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#050521', height: '40px', minWidth: '120px' }}
                >
                  <option value="">Status</option>
                  <option value="in_transit">In Transit</option>
                  <option value="rerouted">Rerouted</option>
                  <option value="completed">Completed</option>
                  <option value="in_progress">In Progress</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#050521' }} />
              </div>

              <div className="relative">
                <select
                  value={customerFilter}
                  onChange={(e) => { setCustomerFilter(e.target.value); setCurrentPage(1) }}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#050521', height: '40px', minWidth: '140px' }}
                >
                  <option value="">Customer</option>
                  {uniqueCustomers.map((customer) => (
                    <option key={customer} value={customer!}>{customer}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ width: '16px', height: '16px', opacity: 0.8, color: '#050521' }} />
              </div>

            </div>
          </div>
        </div>

        {/* Loading / Error States */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12 text-red-500">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && dispatches.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <p style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A' }}>No dispatches found</p>
            <button
              onClick={() => { setEditingDispatch(null); setIsAddDispatchModalOpen(true) }}
              className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#FFFFFF', backgroundColor: '#212126' }}
            >
              <FiPlus className="w-4 h-4" />
              Add First Dispatch
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && dispatches.length > 0 && (
        <div className="overflow-x-auto px-2 sm:px-4" style={{ minWidth: '0' }}>
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '2000px' }}>
            <thead>
              <tr>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderLeft: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap', borderTopLeftRadius: '8px' }}>Warehouse</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Product</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Lot</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Qty Dispatched</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Customer</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Destination</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Dispatch Date</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Status</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Dispatch Instruction</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Proof of Delivery</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Transporter Invoice</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }} title="Transport cost only">Dispatch Costs (transport)</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Sales Invoice</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Dispatch Note</th>
                <th className="text-left py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '11px', lineHeight: '20px', color: '#69697A', paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap', borderTopRightRadius: '8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((item, rowIndex) => {
                const statusColors = getStatusColor(item.status)
                const isExpanded = expandedRows.has(rowIndex)
                const hasDetails = item.details && item.details.length > 0
                const truckCount = getTruckRegistrationEntries(item.details).length

                return (
                  <>
                    <tr 
                      key={item.id} 
                      className="hover:bg-gray-50 transition-colors"
                      style={{ borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', backgroundColor: isExpanded ? '#F2F4F2' : '#FFFFFF' }}
                    >
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderLeft: '1px solid #E9E9EA', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleRowExpansion(rowIndex)}
                            className="flex items-center justify-center hover:opacity-80 transition-opacity"
                            style={{ width: '20px', height: '20px', padding: '0' }}
                            title="Show truck registration and details"
                          >
                            {isExpanded ? (
                              <FiChevronDown style={{ width: '16px', height: '16px', color: '#212126' }} />
                            ) : (
                              <FiChevronRight style={{ width: '16px', height: '16px', color: '#212126' }} />
                            )}
                          </button>
                          <span>{item.warehouse?.name || '—'}</span>
                          {truckCount > 0 && (
                            <span
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontSize: '11px',
                                color: '#39553A',
                                backgroundColor: '#E8F7EF',
                                borderRadius: '4px',
                                padding: '2px 6px',
                              }}
                            >
                              {truckCount} truck{truckCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{item.product || '—'}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{item.lotNumber || '—'}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{formatQty(item.qtyDispatched, item.qtyUnit)}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{item.customer || '—'}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{item.destination || '—'}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{formatDate(item.dispatchDate)}</td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        <span className="inline-flex items-center text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: statusColors.textColor, backgroundColor: statusColors.bgColor, borderRadius: '6px', padding: '4px 12px', whiteSpace: 'nowrap' }}>
                          {getStatusLabel(item.status)}
                        </span>
                      </td>
                      {/* Document buttons */}
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        {renderDocumentButton(item, 'dispatchInstruction')}
                      </td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        {renderDocumentButton(item, 'proofOfDelivery')}
                      </td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        {renderDocumentButton(item, 'transporterInvoice')}
                      </td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>{formatCurrency(item.dispatchCosts, item.dispatchCostsCurrency)}</td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        {renderDocumentButton(item, 'salesInvoice')}
                      </td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        {renderDocumentButton(item, 'dispatchNote')}
                      </td>
                      <td className="py-2 sm:py-3" style={{ paddingLeft: '12px', paddingRight: '12px', borderRight: '1px solid #E9E9EA', borderBottom: isExpanded ? 'none' : '1px solid #E9E9EA' }}>
                        <div className="relative inline-flex">
                          <button
                            onClick={() => handleToggleActions(rowIndex)}
                            className="flex items-center justify-center hover:opacity-80 rounded transition-colors"
                            style={{ backgroundColor: '#EAF1FF', width: '24px', height: '24px', padding: '0' }}
                          >
                            <img src="/images/linearDots.svg" alt="Actions" style={{ width: '14px', height: '14px', filter: 'invert(39%) sepia(84%) saturate(2377%) hue-rotate(204deg) brightness(98%) contrast(101%)' }} />
                          </button>
                          {openActionRow === rowIndex && (
                            <div className="absolute right-0 mt-2 z-10" style={{ minWidth: '140px', backgroundColor: '#FFFFFF', border: '1px solid #E9E9EA', borderRadius: '8px', boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.08)', padding: '6px', top: '100%' }}>
                              {canManageDispatches && (
                              <button onClick={() => handleEditDispatch(item)} className="flex items-center gap-2 w-full hover:opacity-80" style={{ padding: '8px', fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}>
                                <span className="inline-flex items-center justify-center rounded" style={{ width: '24px', height: '24px', backgroundColor: '#FEF9EB' }}>
                                  <img src="/images/edit.svg" alt="Edit" style={{ width: '14px', height: '14px' }} />
                                </span>
                                Edit
                              </button>
                              )}
                              <button onClick={() => { handleViewDispatch(item); setOpenActionRow(null) }} className="flex items-center gap-2 w-full hover:opacity-80" style={{ padding: '8px', fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}>
                                <span className="inline-flex items-center justify-center rounded" style={{ width: '24px', height: '24px', backgroundColor: '#EAF1FF' }}>
                                  <img src="/images/eye.svg" alt="View" style={{ width: '14px', height: '14px' }} />
                                </span>
                                View
                              </button>
                              {canManageDispatches && (
                              <>
                              <button onClick={() => handleAddTruckRegistration(item)} className="flex items-center gap-2 w-full hover:opacity-80" style={{ padding: '8px', fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}>
                                <span className="inline-flex items-center justify-center rounded" style={{ width: '24px', height: '24px', backgroundColor: '#E8F7EF' }}>
                                  <FiPlus className="w-3.5 h-3.5" />
                                </span>
                                Add Truck Registration
                              </button>
                              <button onClick={() => handleAddSubData(item)} className="flex items-center gap-2 w-full hover:opacity-80" style={{ padding: '8px', fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}>
                                <span className="inline-flex items-center justify-center rounded" style={{ width: '24px', height: '24px', backgroundColor: '#EAF1FF' }}>
                                  <FiPlus className="w-3.5 h-3.5" />
                                </span>
                                Add Detail
                              </button>
                              <button
                                onClick={() => handleDeleteDispatch(item)}
                                disabled={deletingDispatchId === item.id}
                                className="flex items-center gap-2 w-full hover:opacity-80 disabled:opacity-60"
                                style={{ padding: '8px', fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}
                              >
                                <span className="inline-flex items-center justify-center rounded" style={{ width: '24px', height: '24px', backgroundColor: '#FEE2E2' }}>
                                  <FiTrash2 className="w-3.5 h-3.5" style={{ color: '#E93323' }} />
                                </span>
                                {deletingDispatchId === item.id ? 'Deleting...' : 'Delete'}
                              </button>
                              </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded: Truck Registration + optional full details */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={15} style={{ padding: '0', borderLeft: '1px solid #E9E9EA', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA' }}>
                          <TruckRegistrationTable
                            stockDispatchId={item.id}
                            details={item.details}
                            onAdd={() => handleAddTruckRegistration(item)}
                            onChanged={fetchDispatches}
                          />
                          {hasDetails && (
                          <div className="overflow-x-auto" style={{ borderTop: '1px solid #E9E9EA' }}>
                            <div style={{ padding: '12px 20px 4px', fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '14px', color: '#212126' }}>
                              Dispatch Details
                            </div>
                            <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                              <thead>
                                <tr style={{ backgroundColor: '#9CAA9D' }}>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Bags</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Tonnage</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Lot number</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>PH / PI</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Contract Number</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Booking Number</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Product</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Forwarder</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Container</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Received</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Free Storage Days</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Storage Start</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Dispatched Date</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Destination</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Transporter</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Truck Rate</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Truck Reg</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Invoice Number</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Chargeable Days</th>
                                  <th className="text-left py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#FFFFFF', paddingLeft: '12px', paddingRight: '12px', borderBottom: '1px solid #E9E9EA', whiteSpace: 'nowrap' }}>Days in Stock</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.details!.map((detail, detailIndex) => (
                                  <tr key={detail.id} style={{ borderBottom: detailIndex === item.details!.length - 1 ? 'none' : '1px solid #E9E9EA', backgroundColor: '#F8F9FB' }}>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.bags ?? '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.tonnage ?? '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.lotNumber || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.phPi || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.contractNumber || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.bookingNumber || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.product || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.forwarder || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.container || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{formatDate(detail.receivedDate)}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.freeStorageDays ?? '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{formatDate(detail.storageStartDate)}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{formatDate(detail.dispatchedDate)}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.destination || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.transporter || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.truckRate ?? '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.truckReg || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.invoiceNumber || '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.chargeableDays ?? '—'}</td>
                                    <td className="py-2 text-xs" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' }}>{detail.daysInStock ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
              {/* Pagination Row */}
              <tr>
                <td colSpan={15} className="py-3 sm:py-4 px-2 sm:px-4" style={{ paddingLeft: '12px', paddingRight: '12px', borderTop: '1px solid #E9E9EA', borderLeft: '1px solid #E9E9EA', borderRight: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <span className="text-xs sm:text-sm" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#69697A' }}>
                      Showing {Math.min(resultsPerPage, totalResults)} of {totalResults} Results
                    </span>
                    <div className="flex items-center gap-2">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', border: '1px solid #E9E9EA', backgroundColor: 'transparent' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#212126" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className="px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity text-xs sm:text-sm"
                          style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: currentPage === page ? '#FFFFFF' : '#212126', backgroundColor: currentPage === page ? '#212126' : 'transparent', border: '1px solid #E9E9EA' }}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                        style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#212126', border: '1px solid #E9E9EA', backgroundColor: 'transparent' }}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#212126" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Modals */}
      <DispatchDetailsModal
        isOpen={isDispatchDetailsModalOpen}
        onClose={handleCloseDispatchDetails}
        dispatchData={selectedDispatch}
        onDocumentClick={(dispatchId, type) => {
          const item =
            selectedDispatch?.id === dispatchId
              ? selectedDispatch
              : dispatches.find((d) => d.id === dispatchId)
          if (item) handleDocumentClick(item, type)
        }}
        onAddTruck={(dispatch) => {
          handleAddTruckRegistration(dispatch)
          setIsDispatchDetailsModalOpen(false)
        }}
        onTruckDataChanged={fetchDispatches}
        onFlyteUpdated={fetchDispatches}
      />

      {documentFilesModal && (
        <DispatchDocumentFilesModal
          isOpen
          onClose={() => setDocumentFilesModal(null)}
          documentType={documentFilesModal.type}
          files={listDocumentFilesFromDispatch(documentFilesModal.dispatch, documentFilesModal.type)}
          onViewFile={(fileId) =>
            handleViewDocument(documentFilesModal.dispatchId, documentFilesModal.type, fileId)
          }
          onAddFiles={() => {
            handleUploadClick(documentFilesModal.dispatchId, documentFilesModal.type)
          }}
          onDeleteFile={(fileId) => void handleDeleteDocumentFile(fileId)}
          deletingFileId={deletingFileId}
        />
      )}
      
      <AddDispatchModal
        isOpen={isAddDispatchModalOpen}
        onClose={() => { setIsAddDispatchModalOpen(false); setEditingDispatch(null) }}
        onSuccess={handleDispatchCreated}
        warehouses={warehouses}
        editDispatch={editingDispatch}
      />
      
      <AddSubDataModal
        isOpen={isAddSubDataModalOpen}
        onClose={() => { setIsAddSubDataModalOpen(false); setSelectedDispatchForSubData(null) }}
        onSuccess={handleSubDataAdded}
        stockDispatchId={selectedDispatchForSubData?.id}
      />

      <AddTruckRegistrationModal
        isOpen={isAddTruckModalOpen}
        onClose={() => { setIsAddTruckModalOpen(false); setSelectedDispatchForTruck(null) }}
        onSuccess={handleTruckRegistrationAdded}
        stockDispatchId={selectedDispatchForTruck?.id}
      />

      <PdfViewerModal
        isOpen={pdfViewer.loading || !!pdfViewer.url}
        onClose={() => setPdfViewer({ url: null, title: 'Document', loading: false })}
        pdfUrl={pdfViewer.url}
        title={pdfViewer.title}
        isLoading={pdfViewer.loading}
        errorMessage={pdfViewer.loading || pdfViewer.url ? null : 'Could not open this document.'}
      />

    </div>
  )
}

export default StockDispatch
